/**
 * @Author: zachalam
 * @Date:   2017-01-23T15:07:27-08:00
 * @Last modified by:   zachalam
 * @Last modified time: 2017-01-24T12:06:20-08:00
 */

import * as Sentry from '@sentry/react';
import * as sdk from '../sdk';
import { getProfileSettings, refreshAccount, refreshProfile, setDarkMode } from './profileActions';
import {
  storeErrors,
  clearErrors,
  setLoading,
  killLoading,
  forgotPasswordSending,
  clearEverything,
  storeSpecificErrors,
} from './commonActions';
import { clearNotificationMessage, displayFailMessage } from './notificationActions';
import { toaster, browserHistory, getLocalItem, removeLocalItem, updatePageTitle, setLocalItem } from '../common';
import store, { AppDispatch, RootState } from '../store';
import { APPLE_AUTH, GOOGLE_AUTH } from '../config/Master';
import { vysionService } from '../utils/vysion/vysionService';
import { isEmpty, isQAautomation, logException, removeCookie, setCookie } from '../utils/helpers';
import { NavigateFunction } from 'react-router-dom';
import { removeIPQSCookies } from '../utils/ipQualityScore';
import { IAccount, IError } from '../types/common';
import { IAttemptSocialLoginFormData } from './types/authActions';
import { mixpanelTrackEvent, mixpanelTrackEventAnonymously } from '../utils/mixpanel/common';
import { EVENT_NAMES } from '../constants/mixpanel';
import {
  PWA_GLOBAL_INSTALLATION_PROMPT_LAST_SEEN_AT_LOCAL_STORAGE_KEY,
  PWA_GLOBAL_INSTALLATION_PROMPT_SHOWN_COUNT,
  PWA_MESSAGE_PAGE_INSTALLATION_PROMPT_LAST_SEEN_AT_LOCAL_STORAGE_KEY,
} from '../utils/pwa';
import { clearLocalStorageExcept } from '../localStorage';
import { clearVWOObj } from '../utils/vwo';
import {
  RESET_CHANGE_EMAIL_FETCHING,
  RESET_OTP_STATE,
  SAVE_CHANGE_EMAIL_VERIFY_DATA,
  SAVE_EMAIL_VERIFY_DATA,
  SET_CHANGE_EMAIL_FETCHING,
  SET_EMAIL_VERIFY_WALL_DATA,
  SET_OTP_STATE,
} from './actionType';
import { changeAccountEmail } from '../sdk/SettingsSDK';
import { handleLoginVysionEvent } from '../utils/vysion/login';
import { VYSION_ACTIONS } from '../utils/vysion';
import { LoginViewVysionEventsEnum } from '../utils/vysion/login/view';
import _capitalize from 'lodash/capitalize';

export const saveToken = (payload: any): any => {
  return {
    type: 'LOGIN_USER',
    payload,
  };
};

export const setEmailVerifyData = (payload: any) => {
  return {
    type: SAVE_EMAIL_VERIFY_DATA,
    payload,
  };
};

export const setOTPState = () => {
  return {
    type: SET_OTP_STATE,
  };
};

export const resetOtpState = () => ({
  type: RESET_OTP_STATE,
});

export const setAuthLoading = (): { type: string } => {
  return {
    type: 'IS_FETCHING',
  };
};

export const resetAuthLoading = (): { type: string } => {
  return {
    type: 'RESET_IS_FETCHING',
  };
};

export const setResetTokenValidity = (payload: any): any => {
  return {
    type: 'RESET_TOKEN_VALIDITY',
    payload,
  };
};

export const setChangeEmailVerifyData = (payload: any) => {
  return {
    type: SAVE_CHANGE_EMAIL_VERIFY_DATA,
    payload,
  };
};

export const setChangeEmailLoading = () => ({
  type: SET_CHANGE_EMAIL_FETCHING,
});

export const resetChangeEmailLoading = () => ({
  type: RESET_CHANGE_EMAIL_FETCHING,
});

export const storeReloaded =
  (): any =>
  (dispatch: any): any => {
    dispatch({
      type: 'STORE_RELOADED',
    });
    return Promise.resolve();
  };

export const removeBlockFlags = (): any => ({
  type: 'AUTH_REMOVE_BLOCK_PROPERTIES',
});

export const setLogout = (flag: boolean): { type: string; payload: boolean } => {
  return {
    type: 'IS_LOGOUT',
    payload: flag,
  };
};

export const temporaryLogin = (payload: any): any => {
  return {
    type: 'TEMPORARY_LOGIN_USER',
    payload,
  };
};

export const setVerifyWallLoading = (): { type: string } => {
  return {
    type: 'IS_VERIFY_WALL_FETCHING',
  };
};

export const resetVerifyWallLoading = (): { type: string } => {
  return {
    type: 'RESET_VERIFY_WALL_FETCHING',
  };
};

export const storeEmailVerifyWallData = (payload: any) => {
  return {
    type: SET_EMAIL_VERIFY_WALL_DATA,
    payload,
  };
};

export const showEmailVerifyDataError = (redirectTo: string = 'login_error', message?: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(
      setEmailVerifyData({
        hasError: true,
        redirectTo: redirectTo,
        error: {
          message: message || "Something wen't wrong, Please try again later.",
        },
      })
    );

    setTimeout(() => {
      dispatch(setEmailVerifyData({ redirectTo: 'login_error' }));
    }, 3000);
  };
};

export const refreshUserToken = (tokenPath: any, retryCount = 0): any => {
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1000; // 1 second

  const attemptRefresh = (): Promise<string> => {
    return sdk
      .getNewAuthToken(tokenPath)
      .then(response => {
        // API refresh endpoint always returns { token: ... } which should be an access token
        const accessToken = response.data.token;

        if (!accessToken) {
          return Promise.reject({ message: 'could not refresh' });
        }

        // Save the access token to Redux store
        store.dispatch(saveToken(response.data));
        store.dispatch({ type: 'DONE_REFRESHING_TOKEN' });

        return Promise.resolve(accessToken);
      })
      .catch(error => {
        // Check if this is a retryable error (network/timeout issues)
        const isNetworkError = !error.response;
        const isTimeout = error.code === 'ECONNABORTED' && error.message?.includes('timeout');
        const isCancelled = error.code === 'ERR_CANCELED';
        const isRetryable = isNetworkError || isTimeout;

        // Don't retry cancelled requests or if we've exceeded max retries
        if (isCancelled || !isRetryable || retryCount >= MAX_RETRIES) {
          console.warn('Token refresh failed after retries', {
            retryCount,
            isNetworkError,
            isTimeout,
            isCancelled,
          });
          return Promise.reject({ message: 'could not refresh', originalError: error });
        }

        // Retry after delay
        console.log(`Token refresh failed, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
        return new Promise((resolve, reject) => {
          setTimeout(
            () => {
              refreshUserToken(tokenPath, retryCount + 1)
                .then(resolve)
                .catch(reject);
            },
            RETRY_DELAY * (retryCount + 1)
          ); // Exponential backoff
        });
      });
  };

  const freshTokenPromise = attemptRefresh();

  // Only dispatch REFRESHING_TOKEN on first attempt
  if (retryCount === 0) {
    store.dispatch({ type: 'REFRESHING_TOKEN', payload: freshTokenPromise });
  }

  return freshTokenPromise;
};

export const logoutUserAction = (navigate?: NavigateFunction | null, killToken = true): any => {
  return async (dispatch: AppDispatch) => {
    dispatch(setLoading());
    dispatch(setLogout(true));
    const currentSubscription = getLocalItem('pushSubscription');

    if (killToken && !isEmpty(currentSubscription)) {
      await sdk.unsubscribeWebPush(currentSubscription);
    }

    try {
      if (killToken) {
        await sdk.killAuthToken();
      }

      dispatch(clearEverything());
      updatePageTitle();
      removeCookie('rgstore_user');
      removeCookie('kUserIdentifier');
      setCookie('isAuthenticated', false);
      localStorage.removeItem('state');
      localStorage.removeItem('redirectTo');

      const keysToRetain = [
        PWA_GLOBAL_INSTALLATION_PROMPT_LAST_SEEN_AT_LOCAL_STORAGE_KEY,
        PWA_MESSAGE_PAGE_INSTALLATION_PROMPT_LAST_SEEN_AT_LOCAL_STORAGE_KEY,
        PWA_GLOBAL_INSTALLATION_PROMPT_SHOWN_COUNT,
      ];

      clearLocalStorageExcept(keysToRetain);
      removeIPQSCookies();
      clearVWOObj();

      if (window?.google?.accounts?.id) {
        window.google.accounts.id.disableAutoSelect();
      }

      navigate ? navigate('/login', { replace: true }) : (window.location.href = '/login');
      dispatch(setLogout(true));

      dispatch(setLoading());
      dispatch(setDarkMode({ desktop: false, mobile: false }));
      setTimeout(() => {
        dispatch(setLogout(false));
      }, 2000);
    } catch (e) {
      console.log('error killAuthToken', e);
      dispatch(setLoading());
      dispatch(setLogout(false));
    }
  };
};

export const createAccountAction = (formData: any): any => {
  return () => {
    return sdk.createUserAccount(formData);
  };
};

export const createUserAccountV2Action = (formData: any): any => {
  return () => {
    return sdk.createUserAccountV2(formData);
  };
};

export const requestPassReminder = (email: string): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .requestPassReminder(email)
      .then(() => {
        // password reminder accepted.
        dispatch(clearErrors());
        dispatch(forgotPasswordSending(true, 'success'));
      })
      .catch(error => {
        // not valid email.
        try {
          const { data } = error.response;

          dispatch(storeErrors(data));
        } catch (e) {
          console.error('invalid response.data', e);
        }
        dispatch(forgotPasswordSending(false, 'error'));
      });
  };
};

//passwordless - verify email
export const verifyEmailAction = (
  email: string,
  hCaptcha = '',
  navigate: NavigateFunction,
  emailToken: string
): any => {
  return async (dispatch: AppDispatch) => {
    try {
      //show loader
      dispatch(resetAuthLoading());
      dispatch(setAuthLoading());

      //verify email api
      const verifyEmailResponse = await sdk.verifyEmail(email, hCaptcha);

      if (verifyEmailResponse.status === 200) {
        // call otp request api, once it's succeed redirect to otp screen
        // dispatch(requestOtpAction(email, 'login', '', true));
        const formData = {
          login: email,
          email_token: emailToken,
        };

        dispatch(passwordLessAuthLogin(formData));
      } else {
        dispatch(resetAuthLoading());
        dispatch(setEmailVerifyData({ email, redirectTo: 'email_error' }));
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Login', action: 'Verify Email' },
        extra: {
          requestPayload: { email, hCaptcha, emailToken },
        },
      });

      const errorCode = (error?.response?.data?.errors && error.response.data.errors[0].code) || null;

      if (error.status === 401) {
        if ([40024001].includes(errorCode)) {
          dispatch(
            setEmailVerifyData({
              email,
              redirectTo: 'continue_with_email',
              hasError: true,
              error: {
                errorCode: errorCode,
                message: 'Invalid captcha',
              },
            })
          );
          dispatch(resetAuthLoading());
          return;
        }

        if ([40101010, 40101007, 40101015, 40101014].includes(errorCode)) {
          //fraud deleted and suspended redirect to fraud delete and suspend error handling screen
          dispatch(setEmailVerifyData({ email, redirectTo: 'email_error', errorCode: errorCode }));
          dispatch(resetAuthLoading());
          return;
        }

        if ([40101001, 40101013].includes(errorCode)) {
          mixpanelTrackEventAnonymously({
            eventName: EVENT_NAMES.ACCOUNT_NOT_FOUND,
            payload: {
              eventProperties: {
                Type: 'Event',
              },
            },
          });
          dispatch(setEmailVerifyData({ email, redirectTo: 'account_not_found' }));
          dispatch(resetAuthLoading());
          return;
        }

        if ([40101011].includes(errorCode)) {
          dispatch(setEmailVerifyData({ email, redirectTo: 'continue_with_invalid_email', errorCode: errorCode }));
          dispatch(resetAuthLoading());

          return;
        }
      }

      dispatch(showEmailVerifyDataError('login_error'));
    }
  };
};

//passwordless - request otp
export const requestOtpAction = (
  email: string,
  type: string,
  hCaptcha: string,
  callLoader = false,
  isResendOTP?: boolean,
  isResetOTP?: boolean
): any => {
  return async (dispatch: AppDispatch) => {
    const isVerifyEmailWall = isActionTypeIsVerifyEmailWall(type);

    dispatch(resetOtpState());

    try {
      const requesType = isVerifyEmailWall ? 'change_email' : type;
      const requestOTPResponse = await sdk.requestEmailOtp(
        email,
        requesType,
        hCaptcha,
        isQAautomation(),
        isResendOTP,
        isResetOTP
      );

      if (requestOTPResponse.status === 200) {
        dispatch(setOTPState());

        if (isVerifyEmailWall) {
          dispatch(
            storeEmailVerifyWallData({
              email,
              redirectTo: 'verify_otp',
              is_last_attempt: requestOTPResponse.data.is_last_attempt,
              otpToken: requestOTPResponse.data.token,
            })
          );
        } else {
          dispatch(
            setEmailVerifyData({
              email,
              redirectTo: type === 'change_email' ? 'continue_with_change_email_otp' : 'continue_with_otp',
              is_last_attempt: requestOTPResponse.data.is_last_attempt,
              otpToken: requestOTPResponse.data.token,
            })
          );
        }

        if (callLoader) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());
      } else {
        logException(
          `Login passwordless flow - Request OTP`,
          {
            response: requestOTPResponse,
            requestPayload: {
              email,
              type,
              hCaptcha,
            },
          },
          'warning'
        );
        // send otp error
        dispatch(showEmailVerifyDataError());

        if (callLoader) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Login passwordless flow', action: 'Request OTP catch block' },
        extra: {
          requestPayload: { email, type, hCaptcha },
        },
      });

      const errorCode = (error?.response?.data?.errors && error.response.data.errors[0].code) || null;

      if ([40101011].includes(errorCode)) {
        dispatch(setEmailVerifyData({ email, redirectTo: 'continue_with_invalid_email', errorCode: errorCode }));
        dispatch(resetAuthLoading());

        return;
      }

      if (callLoader) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());

      dispatch(showEmailVerifyDataError());
    }
  };
};

//passwordless - verify email otp
export const verifyEmailOtpAction = (
  token: string,
  email: string,
  otp: string,
  type: string,
  navigate: NavigateFunction,
  callLoader = false
): any => {
  return async (dispatch: AppDispatch) => {
    const isVerifyEmailWall = isActionTypeIsVerifyEmailWall(type);

    if (callLoader) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : setAuthLoading());

    const loginRedirect = type === 'login' ? 'continue_with_otp' : 'continue_with_change_email_otp';

    try {
      const verifyOtpResponse = await sdk.emailOtpVerify({
        token,
        email,
        otp,
        type: isVerifyEmailWall ? 'change_email' : type,
      });

      if (verifyOtpResponse.status === 200) {
        //check passcode match or not
        const emailToken = verifyOtpResponse?.data?.email_token;
        const isLastAttempt = verifyOtpResponse?.data?.isLastAttempt || false;

        if (!isEmpty(emailToken)) {
          if (['change_email', 'verify_email_wall'].includes(type)) {
            dispatch(
              changeNewEmail(
                {
                  email,
                  email_token: emailToken,
                  email_validated: true,
                },
                navigate,
                type
              )
            );

            return;
          }

          dispatch(setAuthLoading());
          dispatch(verifyEmailAction(email, '', navigate, emailToken));
        } else {
          if (callLoader) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());

          if (isVerifyEmailWall) {
            dispatch(storeEmailVerifyWallData({ otpToken: token, email, redirectTo: 'verify_otp', status: 'error' }));
            return;
          }

          dispatch(
            setEmailVerifyData({
              otpToken: token,
              email,
              redirectTo: loginRedirect,
              type,
              is_last_attempt: isLastAttempt,
              status: 'error',
            })
          );
        }
      } else {
        logException(
          `Login passwordless flow - Verify Email OTP failure`,
          { response: verifyOtpResponse, requestPayload: { token, email, otp, type } },
          'warning'
        );

        // send otp error
        dispatch(showEmailVerifyDataError());

        if (callLoader) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Login passwordless flow', action: 'Verify Email OTP catch block' },
        extra: {
          requestPayload: { token, email, otp, type },
        },
      });

      const errorCode = error?.response?.data?.errors && error?.response?.data?.errors[0].code;

      if (callLoader) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());

      if (errorCode === 40022005) {
        if (isVerifyEmailWall) {
          dispatch(
            storeEmailVerifyWallData({
              otpToken: token,
              email,
              redirectTo: 'verify_otp',
              errorCode,
              maxAttamptReached: true,
            })
          );
          return;
        }

        dispatch(
          setEmailVerifyData({ otpToken: token, email, redirectTo: loginRedirect, type, maxAttamptReached: true })
        );
        return;
      }

      if (isVerifyEmailWall) {
        dispatch(
          storeEmailVerifyWallData({ otpToken: token, email, redirectTo: 'verify_otp', status: 'error', errorCode })
        );
        return;
      }

      dispatch(showEmailVerifyDataError());
    }
  };
};

//passwordless - new auth login
export const passwordLessAuthLogin = (formData: {
  login: string;
  email_token?: string;
  password?: string;
  'h-captcha-response'?: string;
}): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    const otpToken = getState().auth?.emailVerifyData?.otpToken ?? null;

    try {
      dispatch(resetAuthLoading());
      dispatch(setAuthLoading());

      const authResponse = await sdk.getFirstAuthToken(formData);

      if (authResponse.data && authResponse.status === 200) {
        // save token
        authResponse?.data?.has_invalid_email
          ? dispatch(temporaryLogin(authResponse.data))
          : dispatch(saveToken(authResponse.data));
        // retrieve logged in profile.
        dispatch(refreshAccount())
          .then((result: IAccount) => {
            if (result?.hash_id) {
              const eventType = formData?.password ? 'Legacy Password' : 'OTP';

              mixpanelTrackEvent({
                eventName: EVENT_NAMES.USER_LOGGED_IN,
                payload: {
                  eventProperties: {
                    Type: eventType,
                  },
                },
              });
            }

            const darkmodeConfig = result?.account_config?.darkmode;
            const redirectTo = getLocalItem('redirectTo');

            dispatch(setDarkMode(darkmodeConfig));
            removeIPQSCookies();
            // clear errors
            dispatch(clearErrors());
            // jwt saved, redirect to dashboard
            setCookie('isAuthenticated', true, true);
            dispatch(clearNotificationMessage());

            if (authResponse?.data?.has_invalid_email) {
              dispatch(setEmailVerifyData({ login: result?.email, redirectTo: 'continue_change_email' }));
              return;
            }

            if (!isEmpty(redirectTo)) {
              removeLocalItem('redirectTo');
              setTimeout(() => {
                window.location.href = redirectTo;
              }, 2000);
            } else if (location !== undefined && location.search !== undefined) {
              const searchQuery = location.search;
              const patname = `/dashboard${searchQuery}`;

              setTimeout(() => {
                window.location.href = patname;
              }, 2000);
            } else {
              setTimeout(() => {
                window.location.href = '/dashboard';
              }, 2000);
            }

            dispatch(setEmailVerifyData(null));
          })
          .catch(() => {
            toaster.error("Something wen't wrong, Please try again later.");
          });

        dispatch(resetAuthLoading());
        dispatch(killLoading());
      } else {
        logException(
          `Login passwordless flow - Auth login failure`,
          {
            response: authResponse,
            requestPayload: {
              login: formData.login,
              emailToken: formData.email_token,
            },
          },
          'warning'
        );

        dispatch(resetAuthLoading());
        dispatch(showEmailVerifyDataError());
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Login passwordless flow', action: 'Auth login' },
        extra: {
          requestPayload: { login: formData.login, emailToken: formData.email_token },
        },
      });

      dispatch(resetAuthLoading());
      const errorCode = error?.response?.data?.errors && error?.response?.data?.errors[0].code;

      if (error.status === 401) {
        if (formData?.password && [40024001].includes(errorCode)) {
          dispatch(
            setEmailVerifyData({
              email: formData.login,
              otpToken: otpToken,
              redirectTo: 'none',
              hasError: true,
              error: {
                errorCode: errorCode,
                message: 'Invalid captcha',
              },
            })
          );
          return;
        }

        if ([40101001, 40001906, 40101009].includes(errorCode)) {
          dispatch(
            setEmailVerifyData({
              email: formData.login,
              otpToken: otpToken,
              redirectTo: 'none',
              hasError: true,
              error: {
                errorCode: errorCode,
                message: 'Invalid email and/or password combination.',
              },
            })
          );
          return;
        }

        if ([40101010, 40101007, 40101015, 40101014].includes(errorCode)) {
          //fraud deleted and suspended redirect to fraud delete and suspend error handling screen
          dispatch(setEmailVerifyData({ email: formData.login, redirectTo: 'email_error', errorCode: errorCode }));
          return;
        }

        dispatch(showEmailVerifyDataError());
      } else {
        dispatch(showEmailVerifyDataError());
      }
    }
  };
};

//passwordless - reset email otp action
export const resetEmailOTPAction = (email: string, type: string, hCaptcha: string): any => {
  return async () => {
    try {
      const resetOtpResponse = await sdk.resetEmailOtp(email, type, hCaptcha);

      if (resetOtpResponse.status !== 200) {
        toaster.error("Something wen't wrong, Please try again later.");
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Passwordless flow - Bot Detection Modal', action: 'Reset email OTP' },
        extra: {
          requestPayload: { email, type, hCaptcha },
        },
      });

      console.warn('error', error);
      toaster.error("Something wen't wrong, Please try again later.");
    }
  };
};

export const newAuthPhoneLogin = (
  phone_number: string,
  otp: string,
  navigate: NavigateFunction,
  location: Location
): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setAuthLoading());
    return sdk
      .requestOtpAuthVerify({ phone_number, otp })
      .then(response => {
        // save token
        dispatch(saveToken(response.data));

        // retrieve logged in profile.
        dispatch(refreshProfile()).then((result: IAccount) => {
          if (result?.hash_id) {
            mixpanelTrackEvent({
              eventName: EVENT_NAMES.SMS_CODE_VERIFIED,
              payload: {
                eventProperties: {
                  Result: true,
                  Context: 'Login',
                },
              },
            });

            mixpanelTrackEvent({
              eventName: EVENT_NAMES.USER_LOGGED_IN,
              payload: {
                eventProperties: {
                  Type: 'Phone Number',
                },
              },
            });
          }

          const redirectTo = getLocalItem('redirectTo');

          removeIPQSCookies();
          // hide loading
          dispatch(setAuthLoading());
          // clear errors
          dispatch(clearErrors());

          // clear common loading
          dispatch(killLoading());

          if (!isEmpty(redirectTo)) {
            removeLocalItem('redirectTo');
            setTimeout(() => {
              window.location.href = redirectTo;
            }, 2000);
          } else if (location !== undefined && location.search !== undefined) {
            const searchQuery = location.search;

            setTimeout(() => {
              window.location.href = `/dashboard${searchQuery}`;
            }, 2000);
          } else {
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 2000);
          }
        });
      })
      .catch(error => {
        Sentry.captureException(error, {
          tags: { component: 'Login Control flow - Phone login', action: 'Request OTP Auth Verify' },
          extra: {
            requestPayload: { phone_number, otp },
          },
        });
      });
  };
};

//passwordless spec passwordless variant - request otp action
export const requestPhoneOtpAction = (phone_number: any, hCaptcha?: string, isResetOTP = false): any => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAuthLoading());

      const requestPayload = {
        phone_number: `+${phone_number?.phoneNumber}`,
      };

      if (!isEmpty(hCaptcha)) {
        requestPayload['h-captcha-response'] = hCaptcha;
      }

      if (isResetOTP) {
        requestPayload['reset_otp'] = true;
      }

      const requestOTPResponse = await sdk.requestOtpAuth(requestPayload);

      if (requestOTPResponse.status === 200) {
        handleLoginVysionEvent({
          action: VYSION_ACTIONS.VIEW,
          event: LoginViewVysionEventsEnum.VIEW_ENTER_VERIFICATION,
        });

        dispatch(setOTPState());
        dispatch(
          setEmailVerifyData({
            phoneNumber: phone_number,
            redirectTo: 'continue_with_phone_otp',
            is_last_attempt: requestOTPResponse?.data?.is_last_attempt,
          })
        );
        dispatch(resetAuthLoading());
      } else {
        logException(
          `Passwordless flow - Phone login Failure`,
          { response: requestOTPResponse, requestPayload: { phone_number: phone_number?.phoneNumber } },
          'warning'
        );

        // send otp error
        dispatch(showEmailVerifyDataError());
        dispatch(resetAuthLoading());
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Passwordless flow - Phone login', action: 'Request Phone OTP' },
        extra: {
          requestPayload: { phone_number: phone_number?.phoneNumber },
        },
      });

      dispatch(resetAuthLoading());
      console.warn('error', error);

      const errorMapping = {
        '42221084': 'The phone number field is required.',
        '42221085': 'The phone number format is invalid.',
      };

      let errorDetail =
        (error?.response?.data?.errors?.legnth > 0 && error?.response?.data?.errors[0]?.detail) ||
        (error?.response?.data?.phone_number?.length > 0 && error?.response?.data?.phone_number[0]) ||
        (error?.response && error?.response?.data && error?.response?.data?.code && error?.response?.data?.code[0]) ||
        error?.response?.data?.error;

      errorDetail = errorMapping[errorDetail] || errorDetail;

      if (!isEmpty(errorDetail)) {
        dispatch(showEmailVerifyDataError('continue_with_phone', errorDetail));
        return;
      }

      dispatch(showEmailVerifyDataError());
    }
  };
};

//passwordless spec passwordless variant - otp auth verify and login action
export const newAuthPhoneLoginV2Action = (
  phone_number: { phoneNumber: string; country: string },
  otp: string,
  navigate: NavigateFunction,
  location: Location,
  customErrorHandler?: (error: any) => void
): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setAuthLoading());
    return sdk
      .requestOtpAuthVerify({ phone_number: phone_number.phoneNumber, otp })
      .then(response => {
        // save token
        dispatch(saveToken(response.data));

        // retrieve logged in profile.
        dispatch(refreshProfile()).then((result: IAccount) => {
          if (result?.hash_id) {
            mixpanelTrackEvent({
              eventName: EVENT_NAMES.SMS_CODE_VERIFIED,
              payload: {
                eventProperties: {
                  Result: true,
                  Context: 'Login',
                },
              },
            });

            mixpanelTrackEvent({
              eventName: EVENT_NAMES.USER_LOGGED_IN,
              payload: {
                eventProperties: {
                  Type: 'Phone Number',
                },
              },
            });
          }

          setCookie('isAuthenticated', true, true);
          dispatch(clearNotificationMessage());
          dispatch(clearErrors());

          const redirectTo = getLocalItem('redirectTo');

          removeIPQSCookies();
          // hide loading
          dispatch(resetAuthLoading());
          // clear errors
          dispatch(clearErrors());

          // clear common loading
          dispatch(killLoading());

          if (!isEmpty(redirectTo)) {
            removeLocalItem('redirectTo');

            setTimeout(() => {
              window.location.href = redirectTo;
            }, 2000);
          } else if (location !== undefined && location.search !== undefined) {
            const searchQuery = location.search;

            setTimeout(() => {
              window.location.href = `/dashboard${searchQuery}`;
            }, 2000);
          } else {
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 2000);
          }
        });
      })
      .catch(error => {
        Sentry.captureException(error, {
          tags: { component: 'Login passwordless flow - Phone login', action: 'Request OTP Auth Verify' },
          extra: {
            requestPayload: { phone_number: phone_number?.phoneNumber, country: phone_number?.country, otp },
          },
        });

        dispatch(resetAuthLoading());
        const errorCode = error.response?.data?.code;
        const remainingAttempts = error.response?.data?.remaining_attempts ?? 0;

        // If custom error handler is provided, use it
        if (customErrorHandler && typeof customErrorHandler === 'function') {
          customErrorHandler(error);
        } else {
          // Default error handling
          switch (errorCode) {
            case 0:
              //show passcode doesn't match error
              dispatch(
                setEmailVerifyData({
                  phoneNumber: phone_number,
                  redirectTo: 'none',
                  status: 'error',
                })
              );

              mixpanelTrackEvent({
                eventName: EVENT_NAMES.SMS_CODE_VERIFIED,
                payload: {
                  eventProperties: {
                    Result: false,
                    Error: 'Incorrect Code',
                    Attempts: remainingAttempts,
                    Context: 'Login',
                  },
                },
              });

              break;
            case 40022003: //2004 for otp auth verify
              //maxAttamptReached
              dispatch(
                setEmailVerifyData({
                  phoneNumber: phone_number,
                  redirectTo: 'none',
                  maxAttamptReached: true,
                })
              );
              //show code attempts exceed modal
              mixpanelTrackEvent({
                eventName: EVENT_NAMES.SMS_CODE_VERIFIED,
                payload: {
                  eventProperties: {
                    Result: false,
                    Error: 'Requests Attempts Exceeded',
                    Attempts: 3,
                    Context: 'Login',
                  },
                },
              });
              break;
            default:
              dispatch(showEmailVerifyDataError());
              break;
          }
        }
      });
  };
};

export const verifyOtp = (phone_number: string, otp: string, isSignUp = false): any => {
  return (dispatch: AppDispatch) => {
    return sdk.requestOtpVerify({ phone_number, otp }).then(response => {
      // trying to sign up with existing phone number
      if (response.data?.token && isSignUp) {
        // save token
        dispatch(saveToken(response.data));

        removeLocalItem('join_predata');

        // retrieve logged in profile.
        dispatch(refreshProfile()).then((result: IAccount) => {
          if (result?.hash_id) {
            mixpanelTrackEvent({
              eventName: EVENT_NAMES.SMS_CODE_VERIFIED,
              payload: {
                eventProperties: {
                  Result: true,
                  Context: 'Login',
                },
              },
            });

            mixpanelTrackEvent({
              eventName: EVENT_NAMES.USER_LOGGED_IN,
              payload: {
                eventProperties: {
                  Type: 'Phone Number',
                },
              },
            });
          }

          window.location.href = '/dashboard';
        });

        return response.data;
      }
    });
  };
};

const loginError = (error: IError) => {
  return (dispatch: AppDispatch) => {
    // new password not accepted
    dispatch(setLoading());
    try {
      const { data } = error.response;

      dispatch(storeErrors(data));
    } catch (e) {
      console.warn('error', e);
      window.location.href = '/login';
    }
  };
};

export const setNewPassword = (
  formData: any
  // navigate: NavigateFunction
): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setLoading());
    return sdk
      .setNewPassword(formData)
      .then(response => {
        // save token
        dispatch(saveToken(response.data));
        toaster.success('Password reset');

        dispatch(commonLoginAction('Legacy Password'));
      })
      .catch((error: Error) => {
        loginError(error);
      });
  };
};

export const validateActivationToken = (token: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setResetTokenValidity(true));
    return sdk
      .validateActivationPasswordToken(token)
      .then(response => {
        // valid token
        if (response.status === 200) {
          dispatch(saveToken(response.data));
          dispatch(refreshProfile());

          return response.data;
        }
      })
      .catch(error => {
        dispatch(storeErrors(error.response.data));
        // invalid token
        dispatch(setResetTokenValidity(false));
      });
  };
};

export const validateResetPasswordToken = (token: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch(setResetTokenValidity(true));
    return sdk
      .validateResetPasswordToken(token)
      .then(() => {
        // valid token
        dispatch(setResetTokenValidity(true));
      })
      .catch(() => {
        // invalid token
        dispatch(setResetTokenValidity(false));
      });
  };
};

export const deactivateAccount = (formData: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    return sdk
      .deactivateAccount(getState().auth.jwToken, formData)
      .then(() => {
        // account deactivated, auth killed by server.
        // log user out
        dispatch(storeSpecificErrors('form', { errorCode: 40101006 }));
        localStorage.removeItem('came_from_delete_page');
        dispatch(clearErrors());
        window.location.href = '/login?deactivated=1';
      })
      .catch(error => {
        // account could not deactivate.
        dispatch(
          storeErrors({
            ...(error?.response?.data || {}),
            deactivateAccountFailed: true,
          })
        );
      });
  };
};

export const resendEmail = (): any => {
  return () => {
    return sdk
      .resendEmail()
      .then(() => {
        return true;
      })
      .catch(() => {
        return false;
      });
  };
};

const parseSocialRequest = (
  socialSiteResponse: IAttemptSocialLoginFormData,
  site: string,
  action: string | null | undefined
) => {
  const formData = {
    meta: { authResponse: null, basicProfile: null },
    site,
    token: null,
    email: null,
    action: undefined,
  };

  if (action === 'disconnect') {
    return formData;
  }

  if (site === GOOGLE_AUTH) {
    formData.meta = {
      authResponse: socialSiteResponse.getAuthResponse(),
      basicProfile: socialSiteResponse.getBasicProfile(),
    };
    formData.token = socialSiteResponse.getAuthResponse().id_token;
    formData.email = socialSiteResponse.getBasicProfile().getEmail();
  }

  if (site === APPLE_AUTH) {
    formData.meta = {
      authResponse: socialSiteResponse.authorization.code,
      basicProfile: socialSiteResponse.user || null,
    };
    formData.token = socialSiteResponse.authorization.id_token;
    formData.email = socialSiteResponse.user?.email ?? '';
  }

  return formData;
};

export const attachSocialLogin = (socialSiteResponse: any, site: any, action: any, isChangeEmail = false): any => {
  const payload = isChangeEmail ? socialSiteResponse : parseSocialRequest(socialSiteResponse, site, action);

  payload.action = action;

  return (dispatch: AppDispatch, getState: RootState) => {
    if (isChangeEmail) dispatch(setChangeEmailLoading());

    return sdk
      .attachSocialLogin(getState().auth.jwToken, payload)
      .then(response => {
        let currentSocial = response.data.data ?? response.data;

        if (action === 'disconnect') {
          currentSocial = currentSocial.filter(item => item.social_site_name !== site);
        }

        if (isChangeEmail) setTimeout(() => dispatch(resetChangeEmailLoading()), 200);

        if (action === 'connect') toaster.success(`${_capitalize(site)} account linked successfully!`);

        dispatch({
          type: 'UPDATE_USER_SOCIAL',
          payload: Object.values(currentSocial),
        });
      })
      .catch(({ response }) => {
        if (isChangeEmail) dispatch(resetChangeEmailLoading());

        if (response && response.data && response.data.errors && response.data.errors[0]) {
          dispatch(displayFailMessage({ info: response.data.errors[0].title }));
        }
      });
  };
};

export const validateSocialCredentials = (formData: any, site: string, action?: string | null): any => {
  const payload = parseSocialRequest(formData, site, action);

  return (_dispatch: AppDispatch, getState: RootState) => {
    return sdk.validateSocialCredentials(getState().auth.jwToken, payload);
  };
};

export const saveVirgil = (toSave = false): any => {
  return (dispatch: AppDispatch) => {
    if (toSave) {
      dispatch({
        type: 'SET_VIRGIL',
      });
    } else {
      dispatch({
        type: 'UNSET_VIRGIL',
      });
    }
  };
};

// WYP-6446 refactor just let me in together with disabled password
export const checkEmailTokenValidity = (token: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk.getSignedLinkToken(token).then(response => {
      dispatch(removeBlockFlags());
      return response;
    });
  };
};

export const destroyOtherDeviceSession = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk.destroyOtherDeviceSession().then(({ data }) => {
      if (data && data.token) {
        dispatch(saveToken(data));

        toaster.success('You have successfully logged out from other devices!');
      }
    });
  };
};

export const validateMagicSignup = (queryString: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .validateAutoSignup(queryString)
      .then(response => {
        dispatch(saveToken(response.data));
        dispatch(refreshProfile()).then(() => {
          setTimeout(() => {
            browserHistory.push('/profile/edit?userFirstTimeLogin=1');
            toaster.success('You have successfully created a profile!');
            dispatch(killLoading());
            dispatch(clearErrors());
          }, 800);
        });
      })
      .catch(error => {
        dispatch(killLoading());
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const generateVerificationUrl = (): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .getVerificationStatus()
      .then(response => {
        return response.data.data;
      })
      .catch(error => {
        dispatch(killLoading());
        dispatch(storeErrors(error.response.data));
      });
  };
};

const setCartAbandonAction = (eventType: any, extInfo: any = {}): any => {
  return {
    type: 'CART_ABANDON',
    payload: {
      event: eventType,
      eventData: {
        action: 'click',
        page: eventType === 'DEACTIVATE' ? 'deactivate' : 'gdpr-delete',
        extraInfo: extInfo,
        component: null,
      },
      userData: null,
      deviceData: null,
      extendedData: null,
    },
  };
};

export const sendDeactivationAction = (extInfo: any): any => {
  vysionService(setCartAbandonAction('DEACTIVATE', extInfo));
};

export const sendGDPRDeleteAction = (extInfo: any): any => {
  vysionService(setCartAbandonAction('GDPR_DELETE', extInfo));
};

export const endVerificationSession = (formData: any): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .setVerificationStatus(formData)
      .then(response => {
        return response.data.data;
      })
      .catch(error => {
        dispatch(killLoading());
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const fetchCartAbandonmentQuestions = (title = 'deactivate_account'): any => {
  return (dispatch: AppDispatch) => {
    return sdk
      .fetchSurveyQuestions(title)
      .then(response => {
        return response.data.data;
      })
      .catch(error => {
        dispatch(storeErrors(error.response.data));
      });
  };
};

export const verifyInvalidEmailPassword = (login: string, password: string, captcha?: string): any => {
  return async (dispatch: AppDispatch) => {
    dispatch(setAuthLoading());

    try {
      const response = await sdk.authVerifyPassword({ login, password, 'h-captcha-response': captcha });

      // save token
      dispatch(temporaryLogin(response.data));

      // retrieve logged in profile.
      dispatch(refreshAccount()).then((result: IAccount) => {
        if (result?.hash_id) {
          mixpanelTrackEvent({
            eventName: EVENT_NAMES.USER_LOGGED_IN,
            payload: {
              eventProperties: {
                Type: 'Legacy Password',
              },
            },
          });
        }

        const darkmodeConfig = result?.account_config?.darkmode;

        dispatch(setDarkMode(darkmodeConfig));
        removeIPQSCookies();
        dispatch(resetAuthLoading());
        dispatch(clearErrors());
        dispatch(killLoading());
        dispatch(setEmailVerifyData({ login, redirectTo: 'continue_change_email' }));
      });
    } catch (error) {
      dispatch(resetAuthLoading());

      const errorCode = (error?.response?.data?.errors && error.response.data.errors[0].code) || null;

      dispatch(
        setEmailVerifyData({
          login,
          redirectTo: 'continue_with_invalid_email',
          hasError: true,
          error: {
            errorCode: errorCode,
            message: 'Please submit a valid password',
          },
        })
      );
    }
  };
};

const isActionTypeIsVerifyEmailWall = (type: string): boolean => type === 'verify_email_wall';

export const verifyChangeNewEmail = (email: string, type: string = 'change_email', hasValidEmail = false): any => {
  return async (dispatch: AppDispatch) => {
    const isVerifyEmailWall = isActionTypeIsVerifyEmailWall(type);

    dispatch(isVerifyEmailWall ? setVerifyWallLoading() : setAuthLoading());

    try {
      if (!hasValidEmail) {
        await sdk.verifyNewEmail({ email, type: 'verify_email', via: 'password' });
      }

      dispatch(requestOtpAction(email, type, '', hasValidEmail));

      if (!hasValidEmail) dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Change new email', action: 'verifyChangeNewEmail' },
        extra: {
          requestPayload: {
            email,
            type,
            hasValidEmail,
          },
        },
      });

      dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());

      const errorCode = (error?.response?.data?.errors && error.response.data.errors[0].code) || null;

      if ([401, 422].includes(error.status)) {
        if (isActionTypeIsVerifyEmailWall(type)) {
          dispatch(
            storeEmailVerifyWallData({
              login: email,
              redirectTo: 'verify_email',
              hasError: true,
              error: {
                errorCode: errorCode,
                message: 'Please submit a valid email',
              },
            })
          );
          return;
        }

        dispatch(
          setEmailVerifyData({
            login: email,
            redirectTo: 'continue_change_email',
            hasError: true,
            error: {
              errorCode: errorCode,
              message: 'Please submit a valid email',
            },
          })
        );
        return;
      }

      if (isActionTypeIsVerifyEmailWall(type)) {
        dispatch(
          storeEmailVerifyWallData({
            login: email,
            redirectTo: 'verify_email',
            hasError: true,
            error: {
              errorCode: errorCode,
              message: error.response.data.error,
            },
          })
        );
        return;
      }

      dispatch(
        setEmailVerifyData({
          login: email,
          redirectTo: 'continue_change_email',
          hasError: true,
          error: {
            errorCode: errorCode,
            message: error.response.data.error,
          },
        })
      );
    }
  };
};

export const changeNewEmail = (
  formData: {
    email: string;
    email_validated: boolean;
    email_token?: string;
    password?: string;
    'h-captcha-response'?: string;
  },
  navigate: NavigateFunction,
  type?: string
): any => {
  return async (dispatch: AppDispatch) => {
    const isVerifyEmailWall = type && isActionTypeIsVerifyEmailWall(type);

    dispatch(isVerifyEmailWall ? setVerifyWallLoading() : setAuthLoading());

    try {
      const response = await changeAccountEmail(formData);

      if (response?.data?.meta?.token) {
        dispatch(saveToken(response?.data?.meta));

        // retrieve logged in profile.
        dispatch(refreshAccount())
          .then((result: IAccount) => {
            if (isVerifyEmailWall) {
              store.dispatch(storeEmailVerifyWallData({ email: formData.email, redirectTo: 'verify_success' }));
              dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());
              return;
            }

            if (result?.hash_id) {
              mixpanelTrackEvent({
                eventName: EVENT_NAMES.USER_LOGGED_IN,
                payload: {
                  eventProperties: {
                    Type: 'Legacy Password',
                  },
                },
              });
            }

            const darkmodeConfig = result?.account_config?.darkmode;

            dispatch(setDarkMode(darkmodeConfig));

            const redirectTo = getLocalItem('redirectTo');

            removeIPQSCookies();
            // clear errors
            dispatch(clearErrors());
            // jwt saved, redirect to dashboard
            setCookie('isAuthenticated', true, true);
            dispatch(clearNotificationMessage());
            dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());

            if (!isEmpty(redirectTo)) {
              removeLocalItem('redirectTo');
              setTimeout(() => {
                window.location.href = redirectTo;
              }, 2000);
            } else if (location !== undefined && location.search !== undefined) {
              const searchQuery = location.search;
              const patname = `/dashboard${searchQuery}`;

              setTimeout(() => {
                window.location.href = patname;
              }, 2000);
            } else {
              setTimeout(() => {
                window.location.href = '/dashboard';
              }, 2000);
            }
          })
          .catch(() => {
            dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());
            toaster.error("Something wen't wrong, Please try again later.");
          });
      } else {
        dispatch(isVerifyEmailWall ? resetVerifyWallLoading() : resetAuthLoading());
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Change new email', action: 'changeNewEmail', type: type },
        extra: {
          requestPayload: {
            email: formData.email,
            email_validated: formData.email_validated,
            email_Token: formData.email_token,
            hCaptcha: formData['h-captcha-response'],
          },
        },
      });

      console.warn('error', error);
      dispatch(resetAuthLoading());
    }
  };
};

//passwordless change email flow -> request otp
export const requestChangeEmailOtpAction = (
  email: string,
  type: string,
  redirectTo: string,
  resendPasscodeCaptcha?: string,
  callLoader: boolean = true,
  isResendOTP?: boolean
): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    try {
      callLoader && dispatch(setChangeEmailLoading());
      dispatch(resetOtpState());

      const requestOTPResponse = await sdk.requestEmailOtp(
        email,
        type,
        resendPasscodeCaptcha,
        isQAautomation(),
        isResendOTP
      );

      if (requestOTPResponse.status === 200) {
        dispatch(setOTPState());

        dispatch(
          setChangeEmailVerifyData({
            ...getState().auth.changeEmailVerifyData,
            email,
            redirectTo: redirectTo,
            is_last_attempt: requestOTPResponse.data.is_last_attempt,
            otpToken: requestOTPResponse.data.token,
            hasError: false,
            status: null,
            maxAttamptReached: false,
          })
        );
        dispatch(resetChangeEmailLoading());
      } else {
        dispatch(resetChangeEmailLoading());
        // send otp error
        toaster.error("Something wen't wrong, Please try again later.");
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Change email - Resend Passcode Modal', action: 'requestChangeEmailOtpAction', type: type },
        extra: {
          requestPayload: {
            email,
            type,
            redirectTo,
          },
        },
      });

      console.warn('error', error);
      dispatch(resetChangeEmailLoading());
      toaster.error("Something wen't wrong, Please try again later.");
    }
  };
};

//passwordless - change email flow -> First Step -> email otp verify
export const changeEmailVerifyOtpAction = (
  token: string,
  email: string,
  otp: string,
  type: string,
  redirectTo?: string,
  successRedirect?: string
): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    const changeEmailVerifyData = getState().auth.changeEmailVerifyData;

    try {
      dispatch(setChangeEmailLoading());
      const formData = {
        token,
        otp,
        email,
        type: type,
      };
      const verifyOtpResponse = await sdk.emailOtpVerify(formData);

      if (verifyOtpResponse.status === 200) {
        //check passcode match or not
        const emailToken = verifyOtpResponse?.data?.email_token;

        if (!isEmpty(emailToken)) {
          if (successRedirect === 'success') {
            //call change email api
            dispatch(
              changeEmailUpdateEmailAction(
                changeEmailVerifyData?.email,
                emailToken,
                changeEmailVerifyData?.email_validated,
                changeEmailVerifyData?.validated_token
              )
            );
            return;
          }

          dispatch(
            setChangeEmailVerifyData({
              ...changeEmailVerifyData,
              email_token: emailToken,
              via: 'otp',
              email,
              redirectTo: successRedirect,
              hasError: false,
              status: null,
              maxAttamptReached: false,
            })
          );
        } else {
          dispatch(
            setChangeEmailVerifyData({
              ...changeEmailVerifyData,
              otpToken: token,
              email,
              redirectTo: redirectTo,
              status: 'error',
              hasError: false,
              maxAttamptReached: false,
            })
          );
        }

        dispatch(resetChangeEmailLoading());
      } else {
        // send otp error
        dispatch(resetChangeEmailLoading());
        toaster.error("Something wen't wrong, Please try again later.");
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Settings -> Change email flow', action: 'Email OTP verify' },
        extra: {
          requestPayload: {
            token,
            email,
            otp,
            type,
            redirectTo,
            successRedirect,
          },
        },
      });

      console.warn('error', error);
      dispatch(resetChangeEmailLoading());

      const errorCode = error?.response?.data?.errors && error?.response?.data?.errors[0].code;

      if ([40022005].includes(errorCode)) {
        dispatch(
          setChangeEmailVerifyData({
            ...changeEmailVerifyData,
            otpToken: token,
            email,
            redirectTo: redirectTo,
            hasError: false,
            status: null,
            maxAttamptReached: true,
          })
        );
      } else {
        toaster.error("Something wen't wrong, Please try again later.");
      }
    }
  };
};

//passwordless - change email flow -> First Step -> verify via password
export const changeEmailVerifyPasswordAction = (email: string, password: string, type: string): any => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setChangeEmailLoading());
      const formData = {
        email,
        password,
        type,
      };
      const verifyOtpResponse = await sdk.settingsVerifyPassword(formData);

      if (verifyOtpResponse.status === 200) {
        //check passcode match or not
        const pass_token = verifyOtpResponse?.data?.token;

        if (!isEmpty(pass_token)) {
          dispatch(
            setChangeEmailVerifyData({
              email,
              pass_token: pass_token,
              via: 'password',
              redirectTo: 'change_new_email',
              hasError: false,
              status: null,
              maxAttamptReached: false,
            })
          );
        } else {
          toaster.error("Something wen't wrong, Please try again later.");
        }

        dispatch(resetChangeEmailLoading());
      } else {
        // send otp error
        dispatch(resetChangeEmailLoading());
        toaster.error("Something wen't wrong, Please try again later.");
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'Settings -> Change email flow', action: 'Verify via password', type: type },
        extra: {
          requestPayload: {
            email,
            type,
          },
        },
      });

      console.warn('error', error);
      dispatch(resetChangeEmailLoading());

      if ([422].includes(error.status)) {
        const errorMessge =
          (error?.response?.data?.password && error?.response?.data?.password[0]) || 'Please submit a valid password';

        dispatch(
          setChangeEmailVerifyData({
            email,
            redirectTo: 'none',
            hasError: true,
            error: {
              errorCode: '',
              message: errorMessge,
            },
          })
        );
      } else {
        toaster.error("Something wen't wrong, Please try again later.");
      }
    }
  };
};

//passwordless - change email flow -> After First Step -> verify new email
export const changeEmailValidateNewEmailAction = (
  email: string,
  type: string,
  token: string,
  via: string,
  captcha?: string
): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    try {
      dispatch(setChangeEmailLoading());
      const formData: {
        email: string;
        type: string;
        token?: string;
        email_token?: string;
        via: string;
        'h-captcha-response'?: string;
      } = {
        email,
        type: type,
        via,
      };

      if (via === 'password') {
        formData.token = token;
      } else {
        formData.email_token = token;
      }

      if (!isEmpty(captcha)) {
        formData['h-captcha-response'] = captcha;
      }

      const validateEmailResponse = await sdk.verifyNewEmail(formData);

      if (validateEmailResponse.status === 200) {
        const email_validated = validateEmailResponse?.data?.email_validated;
        const validated_token = validateEmailResponse?.data?.token;

        if (!isEmpty(validated_token)) {
          dispatch(
            setChangeEmailVerifyData({
              ...getState().auth.changeEmailVerifyData,
              email,
              email_validated,
              validated_token,
              hasError: false,
              redirectTo: 'none',
            })
          );
          dispatch(requestChangeEmailOtpAction(email, 'privateChangeEmail', 'change_new_email_otp', '', false));
        } else {
          toaster.error("Something wen't wrong, Please try again later.");
          dispatch(resetChangeEmailLoading());
        }
      } else {
        // send otp error
        dispatch(resetChangeEmailLoading());
        toaster.error("Something wen't wrong, Please try again later.");
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'Settings -> Change email flow',
          action: 'After First Step -> verify new email',
          type: type,
        },
        extra: {
          requestPayload: {
            email,
            type,
            token,
            via,
            hCpatcha: captcha,
          },
        },
      });

      console.warn('error', error);
      dispatch(resetChangeEmailLoading());

      const errorCode = error?.response?.data?.errors && error?.response?.data?.errors[0].code;

      //check invalid email
      if ([40101011].includes(errorCode) || error?.response?.data?.email?.length > 0) {
        dispatch(
          setChangeEmailVerifyData({
            ...getState().auth.changeEmailVerifyData,
            email,
            redirectTo: 'none',
            hasError: true,
            error: {
              errorCode: errorCode,
              message: 'Please submit a valid email',
            },
          })
        );
        return;
      }

      if ([40024001].includes(errorCode)) {
        toaster.error('Invalid Captcha');
        return;
      }

      toaster.error("Something wen't wrong, Please try again later.");
    }
  };
};

//passwordless - change email flow -> Final submit change email action
export const changeEmailUpdateEmailAction = (email, emailToken, emailValidated, validatedToken) => {
  return async (dispatch: AppDispatch) => {
    try {
      const formData = {
        email,
        email_token: emailToken,
        email_validated: emailValidated,
        token: validatedToken,
      };

      const updateEmailResponse = await sdk.changeAccountEmail(formData);

      if (updateEmailResponse.status === 200) {
        if (updateEmailResponse?.data?.meta?.token) {
          dispatch(saveToken(updateEmailResponse.data.meta));
        }

        dispatch(getProfileSettings());
        dispatch(clearErrors());

        setLocalItem('trigger_emailChange', 'true');

        dispatch(
          setChangeEmailVerifyData({
            redirectTo: 'change_email_success',
          })
        );
      } else {
        toaster.error("Something wen't wrong, Please try again later.");
      }

      dispatch(resetChangeEmailLoading());
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'Settings -> Change email flow',
          action: 'submit change email',
        },
        extra: {
          requestPayload: {
            email,
            emailToken,
            emailValidated,
            validatedToken,
          },
        },
      });

      console.warn('error', error);
      dispatch(resetChangeEmailLoading());
      toaster.error("Something wen't wrong, Please try again later.");
    }
  };
};

export const commonLoginAction = (eventType: any) => {
  return async (dispatch: AppDispatch) => {
    // retrieve logged in profile.
    dispatch(refreshAccount())
      .then((result: IAccount) => {
        if (result?.hash_id) {
          mixpanelTrackEvent({
            eventName: EVENT_NAMES.USER_LOGGED_IN,
            payload: {
              eventProperties: {
                Type: eventType,
              },
            },
          });
        }

        const darkmodeConfig = result?.account_config?.darkmode;
        const redirectTo = getLocalItem('redirectTo');

        dispatch(setDarkMode(darkmodeConfig));
        removeIPQSCookies();
        // clear errors
        dispatch(clearErrors());
        // jwt saved, redirect to dashboard
        setCookie('isAuthenticated', true, true);
        dispatch(clearNotificationMessage());

        if (!isEmpty(redirectTo)) {
          setTimeout(() => {
            window.location.href = redirectTo;
          }, 2000);
          removeLocalItem('redirectTo');
        } else if (location !== undefined && location.search !== undefined) {
          const searchQuery = location.search;
          const patname = `/dashboard${searchQuery}`;

          setTimeout(() => {
            window.location.href = patname;
          }, 2000);
        } else {
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 2000);
        }
      })
      .catch(() => {
        toaster.error("Something wen't wrong, Please try again later.");
      });

    dispatch(resetAuthLoading());
    dispatch(killLoading());
  };
};
